import {
  daysAt,
  getTradeTatConfig,
  resolveTradeTurnaround,
  versionOn
} from '../../CustomerScopeForm/tat';

const formatRangeLabel = (lower, upper) => {
  if (upper == null || upper === Infinity) {
    return lower <= 1 ? '1+' : `${lower}+`;
  }
  if (lower == null || lower <= 1) return `≤${upper}`;
  return `${lower}–${upper}`;
};

const formatSetAt = iso => {
  if (!iso) return '';
  try {
    return new Intl.DateTimeFormat(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }).format(new Date(iso));
  } catch {
    return String(iso).slice(0, 10);
  }
};

/** Build A9 picks from a submitted `trades_in_scope` trade. */
export const picksFromRecordTrade = trade => {
  const items = [];
  const options = [];
  const scopeIds = [];
  const modifierAnswers = {};

  const captureMod = m => {
    if (!m) return;
    const opts = m.selected_options || [];
    const text = m.text_value || '';
    if (!opts.length && !text) return;
    const id = m.id || m.modifier_id;
    if (id != null) {
      modifierAnswers[String(id)] = { selected_options: opts, text_value: text };
    }
    opts.forEach(o => options.push(o));
    if (text) options.push(text);
  };

  (trade?.sections || []).forEach(sec => {
    (sec.assigned_scopes || []).forEach(sc => {
      const selected = (sc.elements || []).some(el => el.selected);
      if (!selected && !(sc.modifiers || []).some(hasAnswer)) return;
      const sid = sc.id || sc.scope_id;
      if (sid != null) scopeIds.push(String(sid));
      const name = sc.name || sc.scope_name;
      if (name) items.push(name);
      (sc.modifiers || []).forEach(captureMod);
    });
  });
  (trade?.trade_modifiers || []).forEach(captureMod);
  return { items, options, scopeIds, modifierAnswers };
};

const hasAnswer = m => {
  if (!m) return false;
  if ((m.text_value || '').trim()) return true;
  return (m.selected_options || []).length > 0;
};

/**
 * API 11 — map trades_in_scope.custom_card onto the console block shape.
 */
export const customCardFromApi = (apiCustom, accountPreset) => {
  if (!apiCustom) return null;
  const values = (apiCustom.ranges || []).map(r =>
    r.customer_days == null ? null : Number(r.customer_days)
  );
  const storedInPlace =
    typeof apiCustom.in_place_of === 'string'
      ? apiCustom.in_place_of
      : apiCustom.in_place_of?.name || '';
  return {
    values,
    reason: apiCustom.reason || '',
    by: apiCustom.set_by?.name || apiCustom.set_by?.email || 'Internal',
    date: formatSetAt(apiCustom.set_at),
    setAt: apiCustom.set_at || null,
    // Prefer live account_preset card name; fall back to BE-stored in_place_of when preset detached.
    inPlaceOf: accountPreset?.tat?.name || storedInPlace || '',
    resolvedCardId: accountPreset?.tat?.id != null ? String(accountPreset.tat.id) : null,
    ranges: apiCustom.ranges || []
  };
};

/** Build range labels + day row from a still-active custom_card when account_preset is null. */
export const cfgFromCustomCard = (trade, apiCustom) => {
  const rangeSrc = apiCustom?.ranges || [];
  if (!rangeSrc.length) return null;

  const ranges = rangeSrc.map(r => formatRangeLabel(r.lower_bound, r.upper_bound));
  const upper = rangeSrc.map(r => (r.upper_bound == null ? Infinity : Number(r.upper_bound)));
  const customer = rangeSrc.map(r => (r.customer_days == null ? null : Number(r.customer_days)));
  const cardId = 'custom-orphan';
  const storedInPlace =
    typeof apiCustom.in_place_of === 'string'
      ? apiCustom.in_place_of
      : apiCustom.in_place_of?.name || '';
  const card = {
    id: cardId,
    name: storedInPlace || 'Custom card',
    tier: 1,
    of: null,
    versions: [
      {
        appliesFrom: null,
        customer,
        internal: []
      }
    ]
  };
  const preset = {
    id: 'detached',
    name: '—',
    cardId,
    rung: 1
  };
  return {
    id: String(trade?.trade_id || cardId),
    ranges,
    upper,
    cards: [card],
    presets: [preset],
    singleCard: true,
    source: 'custom_card_orphan',
    rangeBounds: rangeSrc.map(r => ({
      lower_bound: r.lower_bound,
      upper_bound: r.upper_bound
    }))
  };
};

/** Build range labels + day row from API 11 tat.ranges. */
export const cfgFromAccountPreset = (trade, accountPreset) => {
  if (!accountPreset?.tat) return null;
  const rangeSrc = accountPreset.tat.ranges || [];
  if (!rangeSrc.length) return null;

  const ranges = rangeSrc.map(r => formatRangeLabel(r.lower_bound, r.upper_bound));
  const upper = rangeSrc.map(r => (r.upper_bound == null ? Infinity : Number(r.upper_bound)));
  const customer = rangeSrc.map(r => (r.customer_days == null ? null : Number(r.customer_days)));
  const cardId = String(accountPreset.tat.id);
  const card = {
    id: cardId,
    name: accountPreset.tat.name || 'Card',
    tier: accountPreset.position != null ? Number(accountPreset.position) : 1,
    of: null,
    versions: [
      {
        appliesFrom: accountPreset.tat.effective_from || null,
        customer,
        internal: []
      }
    ]
  };
  const preset = {
    id: String(accountPreset.id),
    name: accountPreset.name || 'Preset',
    cardId,
    rung: accountPreset.position != null ? Number(accountPreset.position) : 1
  };
  return {
    id: String(trade?.trade_id || cardId),
    ranges,
    upper,
    cards: [card],
    presets: [preset],
    singleCard: false,
    source: 'account_preset',
    // Raw bounds for PATCH custom_card.ranges.
    rangeBounds: rangeSrc.map(r => ({
      lower_bound: r.lower_bound,
      upper_bound: r.upper_bound
    }))
  };
};

const orphanCustomTurnaround = (trade, apiCustom) => {
  const cfg = cfgFromCustomCard(trade, trade?.custom_card);
  if (!cfg) {
    // Still surface custom metadata so Remove stays reachable even without range rows.
    return {
      cfg: null,
      resolved: null,
      stored: null,
      noCard: false,
      custom: apiCustom,
      presetDetached: true
    };
  }
  const resolved = {
    cfg,
    preset: cfg.presets[0],
    card: cfg.cards[0],
    exact: false,
    why: [],
    colIdx: null,
    days: null
  };
  return {
    cfg,
    resolved,
    stored: null,
    noCard: false,
    custom: apiCustom,
    presetDetached: true
  };
};

/**
 * Prefer API 11 account_preset (+ custom_card); else resolve from catalog/fixtures.
 */
export const resolveRecordTradeTurnaround = (trade, record) => {
  const apiCustom = customCardFromApi(trade?.custom_card, trade?.account_preset);

  // API 11 — account_preset present (including null = no card).
  if (trade && Object.prototype.hasOwnProperty.call(trade, 'account_preset')) {
    const ap = trade.account_preset;
    if (!ap?.tat) {
      // Preset detached/null but custom_card still active — keep it visible + removable.
      if (apiCustom) return orphanCustomTurnaround(trade, apiCustom);
      return { cfg: null, resolved: null, stored: null, noCard: true, custom: null, presetDetached: false };
    }
    const cfg = cfgFromAccountPreset(trade, ap);
    if (!cfg) {
      if (apiCustom) return orphanCustomTurnaround(trade, apiCustom);
      return { cfg: null, resolved: null, stored: null, noCard: true, custom: null, presetDetached: false };
    }
    const resolved = {
      cfg,
      preset: cfg.presets[0],
      card: cfg.cards[0],
      exact: true,
      why: [],
      colIdx: null,
      days: null
    };
    return { cfg, resolved, stored: null, noCard: false, custom: apiCustom, presetDetached: false };
  }

  // Legacy / fixtures until account_preset ships.
  const cfg = getTradeTatConfig(trade);
  const stored =
    (record?.trade_turnarounds || record?.current_version?.trade_turnarounds || []).find(
      t => String(t.trade_id) === String(trade.trade_id)
    ) || null;

  if (!cfg?.cards?.length) {
    if (apiCustom) return orphanCustomTurnaround(trade, apiCustom);
    return { cfg: null, resolved: null, stored, noCard: true, custom: null, presetDetached: false };
  }

  const picks = picksFromRecordTrade(trade);
  let resolved = resolveTradeTurnaround(cfg, picks, {
    typicalStdIdx: null,
    iso: record?.current_version?.created_at?.slice?.(0, 10) || undefined
  });

  if (stored?.card_id && resolved) {
    const card = cfg.cards.find(c => String(c.id) === String(stored.card_id)) || resolved.card;
    const preset = cfg.presets.find(p => String(p.id) === String(stored.preset_id)) || resolved.preset;
    resolved = {
      ...resolved,
      card,
      preset,
      exact: stored.exact_match != null ? !!stored.exact_match : resolved.exact
    };
  }

  return { cfg, resolved, stored, noCard: false, custom: apiCustom, presetDetached: false };
};

export const customerDaysForCard = (card, rangesLen, iso) => {
  const ver = versionOn(card, iso);
  const row = ver?.customer || [];
  return Array.from({ length: rangesLen }, (_, i) => (row[i] != null ? row[i] : null));
};

export const cellLabel = v => (v == null ? 'Quote' : String(v));

/**
 * API 12 — body for set/edit. Bounds come from the trade's account_preset ranges.
 */
export const buildCustomCardSetBody = (tradeId, rangeBounds, customerDays, reason) => ({
  custom_card: {
    trade_id: tradeId,
    ranges: (rangeBounds || []).map((b, i) => ({
      lower_bound: b.lower_bound,
      upper_bound: b.upper_bound,
      customer_days: customerDays[i] == null ? null : Number(customerDays[i])
    })),
    reason
  }
});

export const buildCustomCardRemoveBody = (tradeId, reason) => ({
  custom_card: {
    trade_id: tradeId,
    remove: true,
    reason
  }
});

export { daysAt, versionOn, getTradeTatConfig };
